import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const types = [
  ["privacy", "Privacy Policy"], ["terms", "Terms & Conditions"],
  ["risk", "Risk Disclosure"], ["disclaimer", "Disclaimer"],
];

export default function AdminLegal() {
  useSEO("Admin · Legal");
  const [docs, setDocs] = useState({});

  const load = () => api.get("/admin/legal").then((r) => {
    const map = {};
    r.data.forEach((d) => { map[d.type] = d; });
    setDocs(map);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setField = (type, field, val) => setDocs({ ...docs, [type]: { ...(docs[type] || {}), type, [field]: val } });

  const save = async (type, title) => {
    const d = docs[type] || {};
    try {
      const { data } = await api.put("/admin/legal", { type, title: d.title || title, content: d.content || "", effective_date: d.effective_date || null, published: d.published !== false });
      toast.success(`Saved · v${data.version}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <AdminHeader title="Legal Documents" subtitle="Editable & versioned legal content" />
      <p className="text-xs text-slate-500 mb-4">Do not include invented company/registration/regulatory claims. Use verified content only.</p>
      <Tabs defaultValue="privacy">
        <TabsList className="bg-brand-surface border border-white/10 flex-wrap h-auto">
          {types.map(([t, l]) => <TabsTrigger key={t} value={t} data-testid={`legal-tab-${t}`}>{l}</TabsTrigger>)}
        </TabsList>
        {types.map(([type, label]) => {
          const d = docs[type] || {};
          return (
            <TabsContent key={type} value={type} className="mt-4">
              <div className="card-surface rounded-2xl p-7 max-w-3xl space-y-4">
                {d.version ? <p className="text-xs font-mono text-slate-500">Current version: {d.version}</p> : null}
                <div><Label className="text-slate-300">Title</Label><Input value={d.title ?? label} onChange={(e) => setField(type, "title", e.target.value)} data-testid={`legal-title-${type}`} className="mt-1.5 bg-white/5 border-white/10" /></div>
                <div><Label className="text-slate-300">Effective Date</Label><Input type="date" value={(d.effective_date || "").slice(0, 10)} onChange={(e) => setField(type, "effective_date", e.target.value)} data-testid={`legal-date-${type}`} className="mt-1.5 bg-white/5 border-white/10" /></div>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={d.published !== false} onChange={(e) => setField(type, "published", e.target.checked)} data-testid={`legal-published-${type}`} /> Published</label>
                <div><Label className="text-slate-300">Content</Label><Textarea rows={14} value={d.content || ""} onChange={(e) => setField(type, "content", e.target.value)} data-testid={`legal-content-${type}`} className="mt-1.5 bg-white/5 border-white/10 font-mono text-sm" /></div>
                <Button onClick={() => save(type, label)} className="gradient-brand text-brand-navy font-semibold" data-testid={`save-legal-${type}`}>Save & Version</Button>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
